import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DATA_TAG_DUPLICATE_DRIVE_DATA,
  FETCH_DATA_TAG_DRIVES,
} from "../DataTagConstants";
import "./DataEstateDrives.scss";
import { DriveSensitivityBadge } from "./DriveSensitivityBadge";
import TablePagination from "@material-ui/core/TablePagination";
import QuickFilters from "./QuickFilters";
import DriveDrawer from "./DriveDrawer";
import { numberFormatter } from "../../../components/common/Dashboard/utils";

export const categoryIcons = {
  ORGANIZATIONAL:
    "/dataOwnerViewIcons/categoryIcons/organizationalIcon.svg",
  PERSONAL: "/dataOwnerViewIcons/categoryIcons/personalIcon.svg",
  API: "/dataOwnerViewIcons/categoryIcons/API_SensitivityIcon.svg",
  FINANCIAL: "/dataOwnerViewIcons/categoryIcons/financialIcon.svg",
  MARKETING: "/dataOwnerViewIcons/categoryIcons/marketingIcon.svg",
  MEDICAL: "/dataOwnerViewIcons/categoryIcons/medicalIcon.svg",
  OTHER: "/dataOwnerViewIcons/categoryIcons/Other_SensitivityIcon.svg",
  NO_SENSITIVITY:
    "/dataOwnerViewIcons/categoryIcons/Other_SensitivityIcon.svg",
  NOT_PROCESSED:
    "/dataOwnerViewIcons/categoryIcons/Other_SensitivityIcon.svg",
};

export const FIXED_CATEGORIES = [
  "PERSONAL",
  "MARKETING",
  "FINANCIAL",
  "ORGANIZATIONAL",
  "MEDICAL",
  "API",
  "NO_SENSITIVITY",
  "OTHER",
  "NOT_PROCESSED",
];

export const categoryNames = {
  PERSONAL: "Personal",
  MARKETING: "Marketing",
  FINANCIAL: "Financial",
  ORGANIZATIONAL: "Organizational",
  MEDICAL: "Medical",
  API: "API",
  NO_SENSITIVITY: "No Sensitivity",
  OTHER: "Other",
  NOT_PROCESSED: "Not Processed",
};

function CategoryBreakdown({ typeCounts = [] }) {
  const countMap = typeCounts.reduce((acc, item) => {
    acc[item.type] = item.count;
    return acc;
  }, {});

  const ordered = useMemo(() => {
    return FIXED_CATEGORIES.map((cat) => ({
      cat,
      count: countMap[cat] || 0,
    }))
      .filter((i) => i.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [typeCounts]);

  const visible = ordered.slice(0, 3);
  const remaining = ordered.slice(3);

  return (
    <div className="category-breakdown">
      {visible.map((item) => (
        <div
          key={item.cat}
          className="category-row"
          title={categoryNames[item.cat]}
        >
          <img
            src={categoryIcons[item.cat]}
            className="category-icon"
            alt=""
          />
          <span className="category-count">
            {numberFormatter(item.count)}
          </span>
        </div>
      ))}

      {remaining.length > 0 && (
        <div className="category-row remaining-wrapper">
          <div className="remaining-count">+{remaining.length}</div>

          <div className="remaining-tooltip">
            {remaining.map((item) => (
              <div key={item.cat} className="tooltip-row">
                <img
                  src={categoryIcons[item.cat]}
                  className="tooltip-icon"
                  alt=""
                />
                <span className="tooltip-name">
                  {categoryNames[item.cat]}
                </span>
                <span className="tooltip-count">
                  {numberFormatter(item.count)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Duplicate / Copies summary
 *
 * For non-database drives this is always displayed.
 * If no duplicate information exists, both values default to 0.
 *
 * For databases this component is not rendered.
 */
function DuplicateSummaryStrip({ dupInfo }) {
  const filesDuplicated = Number(dupInfo?.filesDuplicated || 0);
  const mutationCount = Number(dupInfo?.mutationCount || 0);

  return (
    <div className="dup-summary-strip">
      <div className="dup-summary-item duplicate">
        <img
          src="/DataTagIcons/files.svg"
          className="dup-summary-icon"
          alt=""
        />

        <span className="data-estate-drive-sensitivity-title">
          Duplicate Objects :{" "}
        </span>

        <span className="dup-summary-value">
          {numberFormatter(filesDuplicated)}
        </span>
      </div>

      <div className="dup-summary-item mutation">
        <img
          src="/DataTagIcons/file-mutation.svg"
          className="dup-summary-icon"
          alt=""
        />

        <span className="data-estate-drive-sensitivity-title">
          Copies:{" "}
        </span>

        <span className="dup-summary-value">
          {numberFormatter(mutationCount)}
        </span>
      </div>
    </div>
  );
}

function DataEstateDrives({ cardsPerRow = 4 }) {
  const dispatch = useDispatch();

  const isDataBase = useSelector(
    (state) => state.dataTagReducer.isDatabase
  );

  const drivesData = useSelector(
    (state) => state.dataTagReducer.drives?.content || []
  );

  const totalCount = useSelector(
    (state) => state.dataTagReducer.drives?.totalElements || 0
  );

  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id
  );

  const dataSourceType = useSelector(
      (state) => state.dataTagReducer.selectedDataSource?.type,
    );

  const isNTFS = dataSourceType === "NTFS";
  const isApplication = useSelector(
    (state) => state.dataTagReducer.isApplication,
  );

  const summaryDataRaw = useSelector(
    (state) => state.dataTagReducer.categoryCardData
  );

  const duplicateDriveData = useSelector(
    (state) => state.dataTagReducer.duplicateDriveData || []
  );

  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedSensitivity, setSelectedSensitivity] = useState([]);
  const [categoryFilter, setSelectedCategoryFilter] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeDrive, setActiveDrive] = useState(null);

  /**
   * Map duplicate information by actor/enterprise actor.
   */
  const dupLookup = useMemo(() => {
    return duplicateDriveData.reduce((map, entry) => {
      if (entry?.actor?.id) {
        map[entry.actor.id] = {
          mutationCount: Number(entry.mutationCount || 0),
          filesDuplicated: Number(entry.filesDuplicated || 0),
        };
      }

      return map;
    }, {});
  }, [duplicateDriveData]);

  const handleOpenDrawer = (drive) => {
    setActiveDrive(drive);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setActiveDrive(null);
  };

  const handleSelect = (drive) => {
    // application (ticket) sources have no drive sidebar
    if (isApplication) return;
    setSelectedCard(drive.driveName);
    handleOpenDrawer(drive);
  };

  useEffect(() => {
    if (!dataSourceId) return;

    dispatch({
      type: FETCH_DATA_TAG_DRIVES,
      payload: {
        dataSourceId,
        page,
        size: rowsPerPage,
        sensitivity:
          selectedSensitivity.length > 0
            ? selectedSensitivity
            : null,
        category:
          categoryFilter.length > 0
            ? categoryFilter
            : null,
      },
    });

    dispatch({
      type: DATA_TAG_DUPLICATE_DRIVE_DATA,
      payload: {
        dataSourceId,
        page,
        size: rowsPerPage,
      },
    });
  }, [
    dataSourceId,
    page,
    rowsPerPage,
    selectedSensitivity,
    categoryFilter,
    dispatch,
  ]);

  return (
    <div className="data-estate-drives-wrapper">
      <QuickFilters
        showCategory={summaryDataRaw.length > 0}
        categoryValue={categoryFilter}
        onCategoryChange={setSelectedCategoryFilter}
        sensitivityValue={selectedSensitivity}
        onSensitivityChange={setSelectedSensitivity}
        loc="data-estate-drives"
      />

      <div
        className="drives-grid"
        style={{
          gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)`,
        }}
      >
        {drivesData.map((drive, index) => {
          /**
           * Get duplicate information for the current drive.
           *
           * If no matching entry exists, dupInfo is null.
           * DuplicateSummaryStrip handles null by showing 0 / 0.
           */
          const dupInfo = drive.enterpriseActorId
            ? dupLookup[drive.enterpriseActorId] || null
            : null;

          return (
            <div
              key={index}
              className={`drive-card ${
                selectedCard === drive.driveName ? "selected" : ""
              } ${
                dupInfo &&
                (dupInfo.filesDuplicated > 0 ||
                  dupInfo.mutationCount > 0)
                  ? "has-duplicates"
                  : ""
              }`}
              onClick={() => handleSelect(drive)}
              style={isApplication ? { cursor: "default" } : undefined}
            >
              <div className="driveDetails">
                <div className="drive-icon">
                  {isDataBase ? (
                    <img
                      src="/DataTagIcons/table-icon-data-tag.svg"
                      alt="Table"
                    />
                  ) : (
                    <img
                      src="/DataTagIcons/DataEstateFolder.svg"
                      alt="folder"
                    />
                  )}
                </div>

                <div className="driveDetailsSummary">
                  <div className="drive-title">
                    {drive.driveName || "Unnamed Drive"}
                  </div>

                  {/* Files / PDA / Size row */}
                  <div className="drive-summary-container">
                    <div className="drive-files">
                      {isDataBase ? "Columns" : isApplication ? "Tickets" : "Files"}:{" "}
                      {numberFormatter(drive.totalFiles || 0)}
                    </div>

                    {/* PDA and size are not shown for application (ticket) sources */}
                    {!isApplication && (
                      <div className="drive-files">
                        PDA: {isNTFS ? "N/A" : numberFormatter(drive.pdaFiles || 0)}
                      </div>
                    )}

                    {!isDataBase && !isApplication && (
                      <div className="drive-files">
                        Size: {drive.totalFileSize || 0}
                      </div>
                    )}
                  </div>

                  {/*
                    Duplicate summary:
                    - Database: completely hidden
                    - Non-database: always shown
                    - No duplicate data: shows 0 / 0
                  */}
                  {!isDataBase && !isApplication && (
                    <DuplicateSummaryStrip dupInfo={dupInfo} />
                  )}

                  <div>
                    <span className="data-estate-drive-sensitivity-title">
                      Sensitivity
                    </span>

                    <DriveSensitivityBadge drive={drive} />
                  </div>

                  <CategoryBreakdown typeCounts={drive.types} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />

      {!isDataBase && !isApplication && (
        <DriveDrawer
          open={drawerOpen}
          onClose={handleCloseDrawer}
          drive={activeDrive}
          dupInfo={
            activeDrive
              ? dupLookup[activeDrive.enterpriseActorId] || null
              : null
          }
          activeDrive={activeDrive}
        />
      )}
    </div>
  );
}

export default DataEstateDrives;